import React, { useState } from 'react';
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider, } from '@cargomatic/ui';
import { useOrderRtbStats } from './hooks/useOrderRtbStats';
import StatCard from './StatCard';
import isNil from 'lodash/isNil';
import formatMoney from 'utils/formatters/Money';
import CargomaticAlert from 'components/ui/Alert';
import { NO_DATA } from './constants';
export default function OrderRtbStatCards({ orderId }) {
    const { data, isLoading, isError, error } = useOrderRtbStats(orderId);
    const [dismissed, setDismissed] = useState(false);
    // Show hook error as closable alert; otherwise render content
    if (isError && !dismissed) {
        return (React.createElement(CargomaticAlert, { type: "error", canClose: true, onClose: () => setDismissed(true), title: "Unable to load summary" }, (error && error.message) || 'Failed to load order financial summary'));
    }
    if (isLoading || !data) {
        return (React.createElement("div", { className: "flex gap-2 overflow-x-auto pb-2 scrollbar-thin" },
            React.createElement(StatCard, { label: "Revenue", isLoading: true }),
            React.createElement(StatCard, { label: "Carrier Costs", isLoading: true }),
            React.createElement(StatCard, { label: "Vendor Costs", isLoading: true }),
            React.createElement(StatCard, { label: "Gross Profit", isLoading: true }),
            React.createElement(StatCard, { label: "Gross Margin", isLoading: true }),
            React.createElement(StatCard, { label: "Unactioned Pending Revenue", isLoading: true }),
            React.createElement(StatCard, { label: "Unactioned Pending COGS", isLoading: true })));
    }
    return (React.createElement("div", { className: "flex gap-2 overflow-x-auto pb-2 scrollbar-thin" },
        React.createElement(StatCard, { label: "Revenue" }, isNil(data.revenue) ? NO_DATA : formatMoney(data.revenue)),
        React.createElement(StatCard, { label: "Carrier Costs" }, isNil(data.carrierCosts) ? NO_DATA : formatMoney(data.carrierCosts)),
        React.createElement(StatCard, { label: "Vendor Costs" }, isNil(data.vendorCosts) ? NO_DATA : formatMoney(data.vendorCosts)),
        React.createElement(StatCard, { label: "Gross Profit" },
            React.createElement("span", { className: !isNil(data.grossProfit) && data.grossProfit > 0
                    ? 'text-green-600'
                    : undefined }, isNil(data.grossProfit) ? NO_DATA : formatMoney(data.grossProfit))),
        React.createElement(StatCard, { label: "Gross Margin" },
            React.createElement("span", { className: !isNil(data.grossMargin) && data.grossMargin > 0
                    ? 'text-green-600'
                    : undefined }, isNil(data.grossMargin)
                ? NO_DATA
                : `${data.grossMargin.toFixed(2)}%`)),
        React.createElement(StatCard, { label: "Unactioned Pending Revenue" },
            React.createElement(TooltipProvider, null,
                React.createElement(Tooltip, null,
                    React.createElement(TooltipTrigger, { asChild: true },
                        React.createElement("span", { className: "hover:text-blue-600 transition-colors underline decoration-dotted", role: "button", tabIndex: 0 }, isNil(data.unactionedPendingRevenue)
                            ? NO_DATA
                            : formatMoney(data.unactionedPendingRevenue))),
                    React.createElement(TooltipContent, { className: "w-auto p-3", align: "start" },
                        React.createElement("div", { className: "flex flex-col gap-2" },
                            React.createElement("div", { className: "text-sm whitespace-nowrap" },
                                React.createElement("span", { className: "font-medium" }, "Unbilled Approved Revenue:"),
                                ' ',
                                React.createElement("span", { className: "font-bold" }, isNil(data.unbilledApprovedRevenue)
                                    ? NO_DATA
                                    : formatMoney(data.unbilledApprovedRevenue))),
                            React.createElement("div", { className: "text-sm whitespace-nowrap" },
                                React.createElement("span", { className: "font-medium" }, "Pending Accessorial Revenue:"),
                                ' ',
                                React.createElement("span", { className: "font-bold" }, isNil(data.pendingAccessorialRevenue)
                                    ? NO_DATA
                                    : formatMoney(data.pendingAccessorialRevenue)))))))),
        React.createElement(StatCard, { label: "Unactioned Pending COGS" }, isNil(data.unactionedPendingCOGS)
            ? NO_DATA
            : formatMoney(data.unactionedPendingCOGS))));
}
